import React, { useState, useRef, useEffect } from 'react';

const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select...",
  name,
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState(value || '');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        // If user typed something and clicked outside, accept it as custom value
        if (searchTerm && searchTerm !== value) {
          onChange({ target: { name, value: searchTerm } });
        }
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchTerm, value, name, onChange]);

  const handleSelect = (option) => {
    onChange({ target: { name, value: option } });
    setInputValue(option);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setSearchTerm(inputValue);
      setHighlightedIndex(-1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else if (filteredOptions.length > 0) {
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isOpen && filteredOptions.length > 0) {
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        // Select highlighted option
        handleSelect(filteredOptions[highlightedIndex]);
      } else if (searchTerm) {
        // If there's a matching option, select it
        const exactMatch = filteredOptions.find(
          opt => opt.toLowerCase() === searchTerm.toLowerCase()
        );
        
        if (exactMatch) {
          handleSelect(exactMatch);
        } else {
          // Otherwise, accept custom input
          onChange({ target: { name, value: searchTerm } });
          setInputValue(searchTerm);
          setIsOpen(false);
          setSearchTerm('');
          setHighlightedIndex(-1);
        }
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(-1);
    } else if (e.key === 'Tab') {
      // Close dropdown on tab but don't prevent default
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: '' } });
    setInputValue('');
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const styles = {
    wrapper: {
      position: 'relative',
      width: '100%'
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    },
    
    inputFocus: {
      borderColor: '#4a90e2'
    },
    buttonsWrapper: {
      position: 'absolute',
      right: '5px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: '2px'
    },
    clearButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '18px',
      color: '#666',
      padding: '2px 5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    arrow: {
      display: 'inline-block',
      marginLeft: '8px',
      fontSize: '10px',
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
      pointerEvents: disabled ? 'none' : 'auto',
      color: disabled ? '#ccc' : '#333',
    },
    dropdown: {
      position: 'absolute',
      top: 'calc(100% + 2px)',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      maxHeight: '250px',
      overflowY: 'auto'
    },
    option: {
      padding: '10px 12px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'background-color 0.15s'
    },
    optionHover: {
      backgroundColor: '#f0f0f0'
    },
    optionHighlighted: {
      backgroundColor: '#d4e9ff',
      fontWeight: 500
    },
    optionSelected: {
      backgroundColor: '#e8f4fd',
      fontWeight: 500
    },
    noResults: {
      padding: '12px',
      textAlign: 'center',
      color: '#666',
      fontSize: '14px',
      fontStyle: 'italic'
    },
    customHint: {
      padding: '12px',
      textAlign: 'center',
      color: '#4a90e2',
      fontSize: '13px',
      borderTop: '1px solid #eee',
      cursor: 'pointer',
      fontWeight: '500'
    }
  };

  const [hoveredOption, setHoveredOption] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <div style={styles.inputWrapper}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            ...styles.input,
            ...(isFocused ? styles.inputFocus : {})
          }}
          autoComplete="off"
        />
        <div style={styles.buttonsWrapper}>
          {inputValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              style={styles.clearButton}
              tabIndex={-1}
            >
              ×
            </button>
          )}
          <span 
            style={styles.arrow}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            ▼
          </span>
        </div>
      </div>

      {/* Dropdown panel */}
      {isOpen && !disabled && (
        <div style={styles.dropdown}>
          {filteredOptions.length === 0 ? (
            <>
              <div style={styles.noResults}>
                No matching options found
              </div>
              {searchTerm && (
                <div 
                  style={styles.customHint}
                  onClick={() => {
                    onChange({ target: { name, value: searchTerm } });
                    setInputValue(searchTerm);
                    setIsOpen(false);
                    setSearchTerm('');
                    setHighlightedIndex(-1);
                  }}
                >
                  Press Enter or click to use "{searchTerm}"
                </div>
              )}
            </>
          ) : (
            <>
              {filteredOptions.map((option, index) => {
                const isHighlighted = highlightedIndex === index;
                const isSelected = value === option;
                const isHovered = hoveredOption === index;

                return (
                  <div
                    key={index}
                    ref={el => optionRefs.current[index] = el}
                    onClick={() => handleSelect(option)}
                    style={{
                      ...styles.option,
                      ...(isHighlighted ? styles.optionHighlighted : {}),
                      ...(isSelected && !isHighlighted ? styles.optionSelected : {}),
                      ...(isHovered && !isHighlighted && !isSelected ? styles.optionHover : {})
                    }}
                    onMouseEnter={() => {
                      setHoveredOption(index);
                      setHighlightedIndex(index);
                    }}
                    onMouseLeave={() => setHoveredOption(null)}
                  >
                    {option}
                  </div>
                );
              })}
              {searchTerm && !filteredOptions.some(opt => opt.toLowerCase() === searchTerm.toLowerCase()) && (
                <div 
                  style={styles.customHint}
                  onClick={() => {
                    onChange({ target: { name, value: searchTerm } });
                    setInputValue(searchTerm);
                    setIsOpen(false);
                    setSearchTerm('');
                    setHighlightedIndex(-1);
                  }}
                >
                  Press Enter or click to use "{searchTerm}"
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;